import type { TransportHandlers, RealtimeTransport } from './transport';
import { fetchRtdbToken, type SyncCommandInput } from './client';
import { getOrCreateDeviceId, getClientContext } from './device';

// Frame-side client for the SharedWorker RTDB broker (rtdb.worker.ts). This frame
// never opens its own RTDB socket: it connects a MessagePort, hands the worker a
// custom token only if the worker asks for one (i.e. it isn't signed in yet), then
// receives presence/sessions snapshots and forwards commands. The worker — and its
// single socket — survives this frame being destroyed, which is the whole point for
// Gamma's add/remove-on-scroll slide iframes.
//
// Liveness: WebKit (PowerPoint/Safari WebViews) and Chromium terminate an idle
// SharedWorker, and a dead worker is SILENT — a MessagePort fires no close/error
// event, so `port.postMessage` into a corpse is swallowed and a command never lands.
// We detect death with a ping/pong probe rather than a constant heartbeat:
//   - before SENDING a command, probe; if the worker doesn't pong, respawn it and
//     post into the fresh port (the cold worker re-auths and flushes the queued
//     command). This is the only path that must be correct, so we pay the check
//     exactly when it matters instead of polling all the time.
//   - to keep READS (presence/sessions) fresh after a suspend, probe on the events
//     that wake a frozen embed: visibilitychange→visible, online, pageshow.
// `new SharedWorker(name)` reattaches to a live instance or spawns a fresh one, so a
// false alarm just opens another port to the same worker (harmless).
//
// Not covered (deliberately, per "not all the time"): a worker killed mid-session
// while the frame stays foreground-visible fires none of these events. If that shows
// up in practice, add a long (60s+) backstop interval here.

const PONG_TIMEOUT_MS = 4000;

export function startSharedWorkerTransport(
  handlers: TransportHandlers,
  ownerKey: string | null = null
): RealtimeTransport {
  let userId: string | null = null;
  const identity = () => ({
    userId,
    deviceId: getOrCreateDeviceId(),
    context: getClientContext()
  });

  let port: MessagePort | null = null;
  let stopped = false;
  let reconnecting = false;
  // Resolvers for in-flight probes; a 'pong' (or a reconnect) settles them all.
  let pongWaiters: Array<(alive: boolean) => void> = [];

  const settlePongs = (alive: boolean) => {
    const waiters = pongWaiters;
    pongWaiters = [];
    waiters.forEach((resolve) => resolve(alive));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMessage = (event: MessageEvent<any>) => {
    const msg = event.data;
    if (!msg) return;
    switch (msg.type) {
      case 'needToken': {
        // Only the first frame after a cold worker is asked; the rest get snapshots.
        // Echo the request's epoch so the worker can discard a token that was
        // fetched before it re-authed for a new owner (it would belong to the
        // previous one).
        const epoch = msg.epoch;
        fetchRtdbToken().then((res) => {
          if (res && port) {
            port.postMessage({
              type: 'token',
              token: res.token,
              ownerId: res.ownerId,
              identity: identity(),
              epoch
            });
          }
        });
        break;
      }
      case 'presence':
        handlers.onPresence(msg.data);
        break;
      case 'sessions':
        handlers.onSessions(msg.data);
        break;
      case 'pong':
        settlePongs(true);
        break;
      default:
        break;
    }
  };

  const connectPort = () => {
    // Reattaches to a live worker or spawns a fresh one; either way we get a port.
    const worker = new SharedWorker(new URL('./rtdb.worker.ts', import.meta.url), {
      name: 'sa-rtdb'
    });
    port = worker.port;
    port.onmessage = onMessage;
    port.start();
    // expectedOwnerId lets the (possibly long-lived, already signed-in) worker
    // detect that this frame belongs to a different owner and re-auth.
    port.postMessage({ type: 'hello', identity: identity(), expectedOwnerId: ownerKey });
  };

  const reconnect = () => {
    if (stopped || reconnecting) return;
    reconnecting = true;
    settlePongs(false);
    if (port) {
      try {
        port.postMessage({ type: 'bye' }); // drop our slot in the worker's port set
      } catch {
        // port may already be dead
      }
      try {
        port.close();
      } catch {
        // ignore
      }
      port = null;
    }
    connectPort();
    reconnecting = false;
  };

  // Ping the worker and resolve true on pong, false on timeout (assume it's gone).
  const probe = (): Promise<boolean> =>
    new Promise((resolve) => {
      if (stopped || !port) {
        resolve(false);
        return;
      }
      pongWaiters.push(resolve);
      try {
        port.postMessage({ type: 'ping' });
      } catch {
        settlePongs(false);
        return;
      }
      setTimeout(() => {
        const i = pongWaiters.indexOf(resolve);
        if (i !== -1) {
          pongWaiters.splice(i, 1);
          resolve(false);
        }
      }, PONG_TIMEOUT_MS);
    });

  // Guarantee a live port: probe the current one, respawn if it doesn't answer.
  const ensureAlive = async () => {
    if (stopped) return;
    if (!port) {
      connectPort();
      return;
    }
    const alive = await probe();
    if (!alive) reconnect();
  };

  // Re-validate reads when a suspended embed wakes (slide becomes active again,
  // network returns, or the page is restored from bfcache).
  const onWake = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      void ensureAlive();
    }
  };

  connectPort();

  const hasWindow = typeof window !== 'undefined';
  const hasDoc = typeof document !== 'undefined';
  if (hasDoc) document.addEventListener('visibilitychange', onWake);
  if (hasWindow) {
    window.addEventListener('online', onWake);
    window.addEventListener('pageshow', onWake);
  }

  return {
    sendCommand: async (input: SyncCommandInput) => {
      // Probe-then-reconnect so the command lands even if the worker was killed
      // while idle; a cold respawn queues the command and flushes it after sign-in.
      await ensureAlive();
      port?.postMessage({ type: 'command', input });
    },
    setPresenceIdentity: (id: string | null) => {
      userId = id;
      port?.postMessage({ type: 'identity', identity: identity() });
    },
    stop: () => {
      stopped = true;
      settlePongs(false);
      if (hasDoc) document.removeEventListener('visibilitychange', onWake);
      if (hasWindow) {
        window.removeEventListener('online', onWake);
        window.removeEventListener('pageshow', onWake);
      }
      if (port) {
        try {
          port.postMessage({ type: 'bye' });
        } catch {
          // port may already be gone
        }
        try {
          port.close();
        } catch {
          // ignore
        }
        port = null;
      }
    }
  };
}
